import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { Upload, Trash2, RefreshCw, Download, Columns3, RotateCcw, ArrowLeft, Plus, Eye } from 'lucide-react';
import { brioGridTheme } from '../agGridTheme';
import { canonicalUnit } from '../utils/measureUnits';
import { useBoqCostIndex } from '../hooks/useBoqCostIndex';
import { rateAge } from '../utils/rateAge';
import { RateAgePill } from '../components/RateAgePill';
import { TradeRail } from '../components/TradeRail';
import BoqGridHeader from '../components/BoqGridHeader';
import { useBoqGridLayout, COLLAPSED_W, QS_ROW_SELECTION, QS_SELECTION_COL_DEF } from '../hooks/useBoqGridLayout';
import { BrioCard, BrioButton, BrioEmptyState, BrioInput, BrioSelect } from '../../components/BrioComponents';
import { ToolbarIcon } from '../components/ToolbarIcon';
import ImportSampleRatesModal from '../components/ImportSampleRatesModal';
import AddCustomRateModal from '../components/AddCustomRateModal';
import { StickyScrollbar } from '../components/StickyScrollbar';
import SourcePreviewModal from '../components/SourcePreviewModal';

const itemCodeOf = (fullCode) => (fullCode || '').split('.').slice(0, 3).join('.');
const UNTAGGED_KEY = '__untagged__';
const RAIL_WIDTH_KEY = 'boqRatesRailWidth';
const REGION_CURRENCY = { IN: 'INR', NL: 'EUR' };

const fmtNum = (v) => (v == null || v === '' ? '' : Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 }));
const toNum = (v) => (v === '' || v === null || v === undefined ? null : Number(v));

// Adjusted-estimate cell: the escalated estimate with a color-coded age pill
// for the underlying observation (reliability cue), to the right of the value.
function AdjEstCell(p) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'flex-end', gap: 6, width: '100%' }}>
      <span>{p.value == null ? '—' : Math.round(p.value).toLocaleString()}</span>
      <RateAgePill date={p.data.rateDate} />
    </span>
  );
}

// Shared base for the editable numeric rate columns.
const numericCol = {
  type: 'numericColumn',
  valueParser: (p) => toNum(p.newValue),
  valueFormatter: (p) => fmtNum(p.value),
};

// Per-column default widths (also used to restore a column after collapse).
const COL_W = {
  fullCode: 118, shortDescription: 320, unit: 76,
  rateQuoted: 104, supplyRateQuoted: 92, installRateQuoted: 92,
  rateBilled: 100, supplyRateBilled: 92, installRateBilled: 92,
  adjEst: 132, vendorId: 150, rateDate: 116, consultant: 130, source: 190, bidBy: 170,
};
// Code is always frozen (no collapse/freeze toggles on it).
const STATIC_COL_IDS = new Set(['fullCode']);
// Columns that carry the collapse / freeze affordances (everything except the
// checkbox and the always-frozen Code column).
const TOGGLEABLE_COL_IDS = [
  'shortDescription', 'unit', 'rateQuoted', 'supplyRateQuoted', 'installRateQuoted',
  'rateBilled', 'supplyRateBilled', 'installRateBilled', 'adjEst', 'vendorId', 'rateDate', 'consultant', 'source', 'bidBy',
];
const LAYOUT_KEY = 'qs-rates-layout-v1';
const SPLITS_KEY = 'qs-rates-splits';   // splits visibility is rates-specific
const loadSplits = () => {
  try { return localStorage.getItem(SPLITS_KEY) === '1'; } catch { return false; }
};

function RatesPage({ initialQuery = '', returnTo = null, onBack, library, rateApi, sourceApi, vendorDir }) {
  const { vendors, findOrCreateVendor } = vendorDir;
  const { tradeGroupByItem } = library;
  const costIndex = useBoqCostIndex();
  const gridRef = useRef(null);

  // Shared QS grid layout: collapse, freeze, persisted widths/order, checkbox.
  const {
    gridContext, decorateColumns, applySavedOrder, onColumnResized, onColumnMoved, resetLayout,
  } = useBoqGridLayout({
    storageKey: LAYOUT_KEY, defaultWidths: COL_W,
    toggleableColIds: TOGGLEABLE_COL_IDS, staticColIds: STATIC_COL_IDS, gridRef,
    // 2: Source file, eye, Bid by, Consultant, Vendor (Oct 2026).
    orderVersion: 2,
  });

  const [query, setQuery] = useState(initialQuery);
  const [sourceFilter, setSourceFilter] = useState('');
  const [selectedTrade, setSelectedTrade] = useState(null);
  const [showImport, setShowImport] = useState(false);
  const [showAddRate, setShowAddRate] = useState(false);
  // The rate whose source file is open in the preview, if any.
  const [previewRate, setPreviewRate] = useState(null);
  const [bulk, setBulk] = useState({ vendor: '', date: '', consultant: '' });
  const [adjustRegion, setAdjustRegion] = useState('IN');
  const [selectedCount, setSelectedCount] = useState(0);
  const [visibleCount, setVisibleCount] = useState(0);
  const containerRef = useRef(null);
  const gridBoxRef = useRef(null);

  // A code/description cross-link from the Library arrives as initialQuery.
  useEffect(() => { setQuery(initialQuery || ''); }, [initialQuery]);

  const searching = query.trim().length > 0;

  // Today's pricing date for the inflation-adjusted column.
  const targetDate = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const targetLabel = useMemo(() => new Date(targetDate).toLocaleDateString(undefined, { month: 'short', year: 'numeric' }), [targetDate]);
  const adjustCurrency = REGION_CURRENCY[adjustRegion];

  const vendorNameById = useMemo(() => {
    const m = new Map();
    vendors.forEach((v) => m.set(v.id, v.name));
    return m;
  }, [vendors]);
  // agSelectCellEditor option list — '' is the "no vendor" choice.
  const vendorValues = useMemo(() => ['', ...vendors.map((v) => v.id)], [vendors]);

  const tradeKeyOf = useCallback((fullCode) => {
    const tg = tradeGroupByItem.get(itemCodeOf(fullCode));
    return tg ? tg.code : UNTAGGED_KEY;
  }, [tradeGroupByItem]);

  // Trade-group summary for the selector rail — counts every benchmark rate.
  const railGroups = useMemo(() => {
    const m = new Map();
    rateApi.rates.forEach((r) => {
      const tg = tradeGroupByItem.get(itemCodeOf(r.fullCode)) || null;
      const key = tg ? tg.code : UNTAGGED_KEY;
      if (!m.has(key)) m.set(key, { key, tg, count: 0 });
      m.get(key).count += 1;
    });
    return [...m.values()].sort((a, b) => {
      if (!a.tg) return 1;
      if (!b.tg) return -1;
      return a.tg.sortOrder - b.tg.sortOrder;
    });
  }, [rateApi.rates, tradeGroupByItem]);

  // Open on real content: select the first trade once rates have loaded.
  useEffect(() => {
    if (!rateApi.loading && !searching && selectedTrade == null && railGroups.length) {
      setSelectedTrade(railGroups[0].key);
    }
  }, [rateApi.loading, searching, selectedTrade, railGroups]);

  const selectedTradeLabel = useMemo(() => {
    const g = railGroups.find((x) => x.key === selectedTrade);
    return g ? (g.tg ? g.tg.label : 'Untagged') : null;
  }, [railGroups, selectedTrade]);

  // Supply/install splits are hidden by default (rates-specific, persisted on
  // its own key); collapse/freeze/width/order come from the shared grid hook.
  const [showSplits, setShowSplits] = useState(loadSplits);
  useEffect(() => {
    try { localStorage.setItem(SPLITS_KEY, showSplits ? '1' : '0'); } catch { /* ignore */ }
  }, [showSplits]);

  const onGridReady = useCallback((params) => {
    applySavedOrder(params.api);
    setVisibleCount(params.api.getDisplayedRowCount());
  }, [applySavedOrder]);

  // Flat rows for the grid; trade key drives the rail's external filter.
  const rowData = useMemo(() => rateApi.rates.map((r) => ({
    id: r.id,
    fullCode: r.fullCode,
    shortDescription: r.shortDescription,
    unit: r.unit,
    rateQuoted: r.rateQuoted,
    rateBilled: r.rateBilled,
    supplyRateQuoted: r.supplyRateQuoted,
    installRateQuoted: r.installRateQuoted,
    supplyRateBilled: r.supplyRateBilled,
    installRateBilled: r.installRateBilled,
    vendorId: r.vendorId,
    rateDate: r.rateDate,
    consultant: r.sourceConsultant || r.sourceDoc?.consultant || '',
    source: r.sourceFilename || r.sourceDoc?.filename || (r.source === 'manual' ? 'Custom rate' : ''),
    isCustom: r.source === 'manual',
    bidBy: r.contractor || '',
    sourceDocumentId: r.sourceDocumentId,
    sourceSheet: r.sourceSheet,
    sourceRow: r.sourceRow,
    sourcePage: r.sourcePage,
    tradeKey: tradeKeyOf(r.fullCode),
  })), [rateApi.rates, tradeKeyOf]);

  // Stable callback from the hook, so the column definitions are not rebuilt on
  // every render (the hook's return object is new each time).
  const { deleteCustomRates } = rateApi;
  const deleteCustom = useCallback(async (row) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete the custom rate of ${fmtNum(row.rateQuoted ?? row.rateBilled)} on ${row.fullCode}?`)) return;
    const res = await deleteCustomRates([row.id]);
    // eslint-disable-next-line no-alert
    if (!res.success) window.alert(res.error || 'Could not delete the rate.');
  }, [deleteCustomRates]);

  // ─── Column definitions ───────────────────────────────────────────────────
  // Every data column is individually collapsible + freezable via the custom
  // header (BoqGridHeader). Collapse/freeze are applied *declaratively* here —
  // a collapsed column is clamped to a sliver via width/min/maxWidth so the
  // columns to its right slide left (imperative applyColumnState was silently
  // ignored under the theming API). Fixed widths + horizontal scroll, no flex.
  const columnDefs = useMemo(() => {
    const base = [
    {
      field: 'fullCode', headerName: 'Code', width: COL_W.fullCode, pinned: 'left',
      cellStyle: { fontFamily: 'monospace', fontSize: 12 },
    },
    {
      field: 'shortDescription', headerName: 'Description', width: COL_W.shortDescription,
      // Wraps onto as many lines as the description needs, and the row grows
      // to fit, so a long spec reads in full rather than behind a tooltip.
      wrapText: true,
      autoHeight: true,
      cellStyle: { lineHeight: '1.35', paddingTop: 6, paddingBottom: 6, wordBreak: 'break-word' },
    },
    { field: 'unit', headerName: 'Unit', width: COL_W.unit, valueFormatter: (p) => canonicalUnit(p.value) },
    {
      ...numericCol, field: 'rateQuoted', headerName: 'Estimate', width: COL_W.rateQuoted,
      editable: (p) => !(p.data.supplyRateQuoted != null || p.data.installRateQuoted != null),
      cellStyle: (p) => ((p.data.supplyRateQuoted != null || p.data.installRateQuoted != null)
        ? { color: 'var(--brio-text-muted)', textAlign: 'right' } : { textAlign: 'right' }),
      tooltipValueGetter: (p) => ((p.data.supplyRateQuoted != null || p.data.installRateQuoted != null)
        ? 'Combined estimate = supply + install (edit the split columns)' : undefined),
    },
    { ...numericCol, field: 'supplyRateQuoted', headerName: 'Est supply', width: COL_W.supplyRateQuoted, editable: true, hide: !showSplits },
    { ...numericCol, field: 'installRateQuoted', headerName: 'Est install', width: COL_W.installRateQuoted, editable: true, hide: !showSplits },
    {
      ...numericCol, field: 'rateBilled', headerName: 'Tender', width: COL_W.rateBilled,
      editable: (p) => !(p.data.supplyRateBilled != null || p.data.installRateBilled != null),
      cellStyle: (p) => ((p.data.supplyRateBilled != null || p.data.installRateBilled != null)
        ? { color: 'var(--brio-text-muted)', textAlign: 'right' } : { textAlign: 'right' }),
    },
    { ...numericCol, field: 'supplyRateBilled', headerName: 'Tdr supply', width: COL_W.supplyRateBilled, editable: true, hide: !showSplits },
    { ...numericCol, field: 'installRateBilled', headerName: 'Tdr install', width: COL_W.installRateBilled, editable: true, hide: !showSplits },
    {
      colId: 'adjEst', headerName: 'Adj. est', type: 'numericColumn', width: COL_W.adjEst,
      headerTooltip: `Estimate (or the tender rate, where there is no estimate) escalated to ${targetLabel} via the ${adjustRegion} construction cost index`,
      // Bids are recorded as tender rates with no estimate beside them, and they
      // need bringing forward as much as any estimate, so fall back to the tender.
      valueGetter: (p) => costIndex.adjust(p.data.rateQuoted ?? p.data.rateBilled, p.data.rateDate, adjustCurrency, targetDate),
      valueFormatter: (p) => (p.value == null ? '—' : Math.round(p.value).toLocaleString()),
      // Show the adjusted estimate with a small age pill, so the reliability of
      // the underlying observation sits right next to the number it produced.
      cellRenderer: AdjEstCell,
      cellStyle: { textAlign: 'right', fontWeight: 600, color: 'var(--brio-gold, #9F902A)' },
      tooltipValueGetter: (p) => {
        const f = costIndex.factor(p.data.rateDate, adjustCurrency, targetDate);
        const basis = p.data.rateQuoted == null && p.data.rateBilled != null ? 'Tender ' : '';
        const base = f != null ? `${basis}×${f.toFixed(3)} from ${p.data.rateDate || '?'} (${adjustRegion})` : 'No cost index for this date/region';
        const age = rateAge(p.data.rateDate);
        return age ? `${base}\n${age.tooltip}` : base;
      },
    },
    {
      field: 'rateDate', headerName: 'Date', width: COL_W.rateDate, editable: true,
      cellEditor: 'agDateStringCellEditor',
      valueFormatter: (p) => p.value || '—',
      tooltipValueGetter: (p) => rateAge(p.value)?.tooltip,
    },
    { field: 'source', headerName: 'Source file', width: COL_W.source, editable: false, tooltipValueGetter: (p) => p.value },
    // Opens the page or rows of the source file this rate was read from. Shown
    // wherever the import recorded a place in a file; the preview says so when
    // the file itself has not been uploaded yet.
    {
      colId: 'sourcePreview', headerName: '', width: 52, sortable: false, resizable: false,
      headerTooltip: 'Show where in its source file this rate came from',
      cellRenderer: (p) => (p.data?.sourceDocumentId && (p.data.sourcePage || (p.data.sourceSheet && p.data.sourceRow)) ? (
        <button type="button"
          title={p.data.sourcePage ? `Show page ${p.data.sourcePage} of ${p.data.source}` : `Show ${p.data.sourceSheet.trim()} row ${p.data.sourceRow} of ${p.data.source}`}
          aria-label="Show this rate in its source file"
          onClick={() => setPreviewRate(p.data)}
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'var(--brio-text-muted)', display: 'inline-flex', alignItems: 'center', height: '100%' }}>
          <Eye size={15} />
        </button>
      ) : null),
    },
    // The contractor a tender rate was bid by: a comparative BOQ files one rate
    // per bidder against the same item, and this is what tells them apart.
    {
      field: 'bidBy', headerName: 'Bid by', width: COL_W.bidBy, editable: true,
      valueFormatter: (p) => p.value || '—',
      tooltipValueGetter: (p) => p.value || undefined,
    },
    { field: 'consultant', headerName: 'Consultant', width: COL_W.consultant, editable: false },
    {
      field: 'vendorId', headerName: 'Vendor', width: COL_W.vendorId, editable: true,
      cellEditor: 'agSelectCellEditor', cellEditorParams: { values: vendorValues },
      valueFormatter: (p) => (p.value ? (vendorNameById.get(p.value) || '?') : '—'),
      getQuickFilterText: (p) => vendorNameById.get(p.value) || '',
    },
    // Delete, on custom (manual) rates only and only for those allowed to add
    // them. Imported rates are the workbook's figures and stay put.
    ...(rateApi.canAddCustom ? [{
      colId: 'deleteCustom', headerName: '', width: 52, sortable: false, resizable: false,
      cellRenderer: (p) => (p.data?.isCustom ? (
        <button type="button" title="Delete this custom rate" aria-label="Delete this custom rate"
          onClick={() => deleteCustom(p.data)}
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'var(--brio-text-muted)', display: 'inline-flex', alignItems: 'center', height: '100%' }}>
          <Trash2 size={15} />
        </button>
      ) : null),
    }] : []),
    ];
    return decorateColumns(base);
  }, [vendorValues, vendorNameById, costIndex, adjustCurrency, targetDate, targetLabel, adjustRegion, showSplits, decorateColumns, rateApi.canAddCustom, deleteCustom]);

  const defaultColDef = useMemo(() => ({
    sortable: true, filter: false, resizable: true, suppressMovable: false, enableCellChangeFlash: true,
    minWidth: COLLAPSED_W,                       // allow collapsing to a sliver
    headerComponent: BoqGridHeader,
  }), []);

  // ─── Trade + source scoping via an external filter ──────────────────────────
  const isExternalFilterPresent = useCallback(
    () => !!sourceFilter || (!searching && selectedTrade != null),
    [sourceFilter, searching, selectedTrade]
  );
  const doesExternalFilterPass = useCallback((node) => {
    if (sourceFilter && node.data.sourceDocumentId !== sourceFilter) return false;
    if (!searching && selectedTrade != null && node.data.tradeKey !== selectedTrade) return false;
    return true;
  }, [sourceFilter, searching, selectedTrade]);

  useEffect(() => {
    const api = gridRef.current?.api;
    if (!api) return;
    api.onFilterChanged();
    setVisibleCount(api.getDisplayedRowCount());
  }, [sourceFilter, searching, selectedTrade]);

  // ─── Inline edits → persist via the rate API ────────────────────────────────
  const handleCellValueChanged = useCallback(async (e) => {
    const { data, colDef, newValue, oldValue, node } = e;
    if (newValue === oldValue) return;
    const field = colDef.field;
    let patch;

    if (field === 'rateQuoted' || field === 'rateBilled') {
      patch = { [field]: toNum(newValue) };
    } else if (['supplyRateQuoted', 'installRateQuoted', 'supplyRateBilled', 'installRateBilled'].includes(field)) {
      // Editing a split component rebuilds cost_split and recomputes that
      // basis's combined rate (mirrors the project-items grid).
      const isBilled = field.endsWith('Billed');
      const q = { supply: toNum(data.supplyRateQuoted), install: toNum(data.installRateQuoted) };
      const b = { supply: toNum(data.supplyRateBilled), install: toNum(data.installRateBilled) };
      (isBilled ? b : q)[field.startsWith('supply') ? 'supply' : 'install'] = toNum(newValue);
      const costSplit = {};
      if (q.supply != null || q.install != null) costSplit.quoted = q;
      if (b.supply != null || b.install != null) costSplit.billed = b;
      patch = { costSplit: Object.keys(costSplit).length ? costSplit : null };
      if (isBilled) patch.rateBilled = (b.supply || 0) + (b.install || 0);
      else patch.rateQuoted = (q.supply || 0) + (q.install || 0);
    } else if (field === 'vendorId') {
      patch = { vendorId: newValue || null };
    } else if (field === 'rateDate') {
      patch = { rateDate: newValue || null };
    } else if (field === 'bidBy') {
      patch = { contractor: newValue || null };
    } else {
      return;
    }

    const result = await rateApi.updateRate(data.id, patch);
    if (!result || result.success === false) {
      node.setDataValue(field, oldValue);
      // eslint-disable-next-line no-alert
      window.alert((result && result.error) || 'Failed to save change.');
    }
  }, [rateApi]);

  const onSelectionChanged = useCallback(() => {
    setSelectedCount(gridRef.current?.api?.getSelectedRows().length || 0);
  }, []);
  const onModelUpdated = useCallback(() => {
    setVisibleCount(gridRef.current?.api?.getDisplayedRowCount() || 0);
  }, []);

  const applyBulk = useCallback(async () => {
    const ids = gridRef.current?.api?.getSelectedRows().map((r) => r.id) || [];
    if (!ids.length) return;
    const patch = {};
    if (bulk.vendor.trim()) {
      const r = await findOrCreateVendor(bulk.vendor);
      if (r.success) patch.vendorId = r.id;
    }
    if (bulk.date) patch.rateDate = bulk.date;
    if (bulk.consultant.trim()) patch.sourceConsultant = bulk.consultant.trim();
    if (Object.keys(patch).length === 0) return;
    await rateApi.bulkUpdate(ids, patch);
    setBulk({ vendor: '', date: '', consultant: '' });
  }, [bulk, findOrCreateVendor, rateApi]);

  const deleteSelected = useCallback(async () => {
    const ids = gridRef.current?.api?.getSelectedRows().map((r) => r.id) || [];
    if (!ids.length) return;
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete ${ids.length} rate${ids.length === 1 ? '' : 's'}?`)) return;
    await rateApi.deleteMany(ids);
    gridRef.current?.api?.deselectAll();
  }, [rateApi]);

  const exportCsv = useCallback(() => {
    gridRef.current?.api?.exportDataAsCsv({
      fileName: `boq-rates-${new Date().toISOString().slice(0, 10)}.csv`,
      processCellCallback: (p) => (p.column.getColId() === 'vendorId' ? (vendorNameById.get(p.value) || '') : p.value),
    });
  }, [vendorNameById]);

  return (
    <div>
      {/* Arrived from a Library rate link: a way back to that item. */}
      {returnTo?.itemCode && onBack && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap',
          padding: '0.45rem 0.75rem', margin: '0.75rem 0', borderRadius: 4,
          background: 'var(--brio-gold-light, rgba(159,144,42,0.12))',
          border: '1px solid var(--brio-gold, #9F902A)',
        }}>
          <button
            type="button"
            onClick={onBack}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 5, cursor: 'pointer',
              background: 'transparent', border: 'none', padding: 0,
              color: 'var(--brio-gold, #9F902A)', fontSize: '0.85rem', fontWeight: 600,
            }}
          >
            <ArrowLeft size={15} /> Back to <code style={{ fontSize: '0.85rem' }}>{returnTo.itemCode}</code>
            {returnTo.description ? <span style={{ fontWeight: 400 }}>· {returnTo.description}</span> : null}
          </button>
          <span style={{ fontSize: '0.78rem', color: 'var(--brio-text-muted)' }}>showing rates for this item</span>
          {rateApi.canAddCustom && (
            <BrioButton variant="secondary" onClick={() => setShowAddRate(true)}
              style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: '0.3rem', padding: '0.3rem 0.7rem', fontSize: '0.82rem' }}>
              <Plus size={14} /> Add custom rate
            </BrioButton>
          )}
        </div>
      )}
      {showAddRate && returnTo?.itemCode && (
        <AddCustomRateModal
          itemCode={returnTo.itemCode}
          versions={(library.versions || []).filter((v) => v.itemCode === returnTo.itemCode)}
          vendors={vendors}
          showSplits={showSplits}
          onSave={rateApi.addCustomRate}
          onClose={() => setShowAddRate(false)}
        />
      )}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flex: 1, minWidth: 240 }}>
          <BrioInput value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search all trades by code, description, vendor, source…" style={{ flex: 1, maxWidth: 340 }} />
          <BrioSelect value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)} style={{ fontSize: '0.82rem' }}>
            <option value="">All sources</option>
            {sourceApi.sources.map((s) => <option key={s.id} value={s.id}>{s.filename || '(manual)'} · {s.documentDate}</option>)}
          </BrioSelect>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span style={{ fontSize: '0.8rem', color: 'var(--brio-text-muted)' }} title={`The "Adj. est" column escalates each estimate (or tender rate, where there is no estimate) from its rate date to ${targetLabel} using the construction cost index`}>
            Adjust to {targetLabel} ·
          </span>
          <BrioSelect value={adjustRegion} onChange={(e) => setAdjustRegion(e.target.value)} style={{ fontSize: '0.8rem' }} title="Cost-index region used for the adjusted column">
            <option value="IN">India (INR)</option>
            <option value="NL">Netherlands (EUR)</option>
          </BrioSelect>
          <BrioButton variant={showSplits ? 'primary' : 'secondary'} onClick={() => setShowSplits((v) => !v)}
            title="Show / hide the supply &amp; install rate columns (Est supply, Est install, Tdr supply, Tdr install)"
            style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', padding: '0.35rem 0.6rem', fontSize: '0.8rem' }}>
            <Columns3 size={14} /> Splits
          </BrioButton>
          <ToolbarIcon onClick={resetLayout} tooltip="Reset column layout (widths, order, collapsed &amp; frozen)"><RotateCcw size={15} /></ToolbarIcon>
          <ToolbarIcon onClick={exportCsv} tooltip="Export CSV"><Download size={15} /></ToolbarIcon>
          <ToolbarIcon onClick={rateApi.refresh} tooltip="Refresh"><RefreshCw size={15} /></ToolbarIcon>
          <BrioButton variant="primary" onClick={() => setShowImport(true)}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', padding: '0.35rem 0.7rem', fontSize: '0.82rem' }}>
            <Upload size={14} /> Import sample BOQ
          </BrioButton>
        </div>
      </div>

      {selectedCount > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', padding: '0.5rem 0.75rem', marginBottom: '0.75rem', background: 'var(--brio-gold-faint, #f7f1d4)', borderRadius: 2 }}>
          <strong style={{ fontSize: '0.85rem' }}>{selectedCount} selected</strong>
          <span style={{ color: 'var(--brio-text-muted)', fontSize: '0.8rem' }}>· bulk set:</span>
          <input list="bulk-vendor-list" value={bulk.vendor} onChange={(e) => setBulk({ ...bulk, vendor: e.target.value })} placeholder="vendor"
            style={{ padding: '0.3rem 0.45rem', border: '1px solid var(--brio-border, #ccc)', borderRadius: 2, fontSize: '0.82rem', width: 150 }} />
          <datalist id="bulk-vendor-list">{vendors.map((v) => <option key={v.id} value={v.name} />)}</datalist>
          <input type="date" value={bulk.date} onChange={(e) => setBulk({ ...bulk, date: e.target.value })}
            style={{ padding: '0.3rem 0.45rem', border: '1px solid var(--brio-border, #ccc)', borderRadius: 2, fontSize: '0.82rem' }} />
          <input value={bulk.consultant} onChange={(e) => setBulk({ ...bulk, consultant: e.target.value })} placeholder="consultant"
            style={{ padding: '0.3rem 0.45rem', border: '1px solid var(--brio-border, #ccc)', borderRadius: 2, fontSize: '0.82rem', width: 150 }} />
          <BrioButton variant="secondary" onClick={applyBulk} style={{ padding: '0.3rem 0.7rem', fontSize: '0.8rem' }}>Apply</BrioButton>
          <ToolbarIcon onClick={deleteSelected} danger tooltip="Delete selected"><Trash2 size={15} /></ToolbarIcon>
        </div>
      )}

      {rateApi.loading ? (
        <BrioCard><div style={{ color: 'var(--brio-text-muted)', padding: '0.5rem' }}>Loading rates…</div></BrioCard>
      ) : rateApi.error ? (
        <BrioCard><div style={{ color: '#a94442' }}>{rateApi.error}</div></BrioCard>
      ) : rateApi.rates.length === 0 ? (
        <BrioCard>
          <BrioEmptyState>
            <div style={{ marginBottom: '0.5rem' }}>No benchmark rates yet.</div>
            <div style={{ fontSize: '0.85rem' }}>Use <strong>Import sample BOQ</strong> to harvest rates with full provenance (date, consultant, vendor, source file).</div>
          </BrioEmptyState>
        </BrioCard>
      ) : (
        <div ref={containerRef} style={{ display: 'flex', alignItems: 'flex-start' }}>
          {/* Trade selector rail — same picker as the Library page */}
          <TradeRail
            groups={railGroups}
            selectedKey={searching ? null : selectedTrade}
            onSelect={(key) => { setQuery(''); setSelectedTrade(key); }}
            storageKey={RAIL_WIDTH_KEY}
            containerRef={containerRef}
          />

          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 600, marginBottom: '0.4rem', minHeight: 20 }}>
              {searching
                ? <>Search results · {visibleCount} rate{visibleCount === 1 ? '' : 's'}</>
                : selectedTradeLabel
                  ? <>{selectedTradeLabel} · {visibleCount} rate{visibleCount === 1 ? '' : 's'}</>
                  : 'Select a trade'}
            </div>
            <StickyScrollbar targetRef={gridBoxRef} selector=".ag-body-horizontal-scroll-viewport" />
            <div ref={gridBoxRef} style={{ width: '100%', height: '72vh' }}>
              <AgGridReact
                ref={gridRef}
                theme={brioGridTheme}
                rowData={rowData}
                columnDefs={columnDefs}
                defaultColDef={defaultColDef}
                context={gridContext}
                onGridReady={onGridReady}
                getRowId={(p) => String(p.data.id)}
                quickFilterText={query}
                isExternalFilterPresent={isExternalFilterPresent}
                doesExternalFilterPass={doesExternalFilterPass}
                onCellValueChanged={handleCellValueChanged}
                onSelectionChanged={onSelectionChanged}
                onModelUpdated={onModelUpdated}
                onColumnResized={onColumnResized}
                onColumnMoved={onColumnMoved}
                rowSelection={QS_ROW_SELECTION}
                selectionColumnDef={QS_SELECTION_COL_DEF}
                cellSelection
                stopEditingWhenCellsLoseFocus
                animateRows={false}
                rowHeight={32}
                headerHeight={34}
              />
            </div>
          </div>
        </div>
      )}

      {previewRate && (
        <SourcePreviewModal rate={previewRate} onClose={() => setPreviewRate(null)} />
      )}

      {showImport && (
        <ImportSampleRatesModal
          versions={library.versions}
          vendors={vendors}
          findOrCreateVendor={findOrCreateVendor}
          createCandidateVersion={library.createCandidateVersion}
          onCreateSource={sourceApi.createSource}
          onCreateRates={rateApi.createRates}
          onClose={() => setShowImport(false)}
          onDone={() => { rateApi.refresh(); sourceApi.refresh(); }}
        />
      )}
    </div>
  );
}

export default RatesPage;
